'use client';

import { useState, useEffect } from 'react';
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { getCompetitionCopy } from "../../lib/competitionCopy";

interface RankingData {
  teamId?: number;
  leagueId?: number;
  teamName?: string;
  leagueName?: string;
  rank: number;
  points?: number;
  score?: number;
}

interface RankingResponse {
  data: RankingData[];
  totalRecords: number;
}

const RECORDS_PER_PAGE = 10;

export default function Ranking() {
  const { competitionData, competitionConfig } = useCompetitionContext();
  const copy = getCompetitionCopy(competitionConfig?.competitionFeed);
  const getDefaultRankableWeekId = () => {
    const { deadlineWeek, rosterEndWeek } = competitionData?.deadlineInfo || {};
    if (typeof deadlineWeek === 'number') {
      return Math.max(1, deadlineWeek - 1);
    }
    if (typeof rosterEndWeek === 'number') {
      return rosterEndWeek;
    }
    return 1;
  };
  
  // State
  const [activeTab, setActiveTab] = useState<'general' | 'week'>('general');
  const [rankings, setRankings] = useState<RankingData[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchName, setSearchName] = useState<string>('');
  const [selectedWeekId, setSelectedWeekId] = useState<number | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  
  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(totalRecords / RECORDS_PER_PAGE));
  
  const tabs = [
    { name: 'Général', key: 'general', current: activeTab === 'general' },
    { name: copy.raceLabelTitle, key: 'week', current: activeTab === 'week' },
  ];

  useEffect(() => {
    if (hasInitialized || !competitionData?.deadlineInfo) return;
    setSelectedWeekId(getDefaultRankableWeekId());
    setHasInitialized(true);
  }, [competitionData, hasInitialized]);

  useEffect(() => {
    if (!hasInitialized) return;
    if (!competitionConfig?.seasonId || !competitionConfig?.competitionFeed) return;

    const abortController = new AbortController();

    const fetchRankings = async () => {
      setIsLoading(true);

      try {
        let url = `/api/rankings?pageRecords=${RECORDS_PER_PAGE}&pageNumber=${pageNumber}&name=${searchName}&competitionFeed=${competitionConfig.competitionFeed}&seasonId=${competitionConfig.seasonId}`;

        if (activeTab === 'week') {
          const weekId = selectedWeekId || 1;
          url += `&weekId=${weekId}`;
        }

        const response = await fetch(url, { signal: abortController.signal });

        if (!response.ok) {
          throw new Error(`Failed to fetch rankings: ${response.statusText}`);
        }

        const data: RankingResponse = await response.json();
        setRankings(data.data);
        setTotalRecords(data.totalRecords);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.error('Error fetching rankings:', error);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchRankings();

    return () => abortController.abort();
  }, [activeTab, pageNumber, searchName, selectedWeekId, selectedTitle, competitionConfig?.competitionFeed, competitionConfig?.seasonId]);

  function classNames(...classes: any) {
    return classes.filter(Boolean).join(' ');
  }

  return (
    <div className="min-h-[calc(100vh-100px)] px-3 pb-20 pt-6 sm:px-4">
      <div className="mx-auto w-full max-w-6xl space-y-6">
      <div>
        <h1 className="mb-2 text-xl font-bold text-white">Classement</h1>
        <p className="text-md text-white/90">
          Consulte le classement général et par {copy.raceLabelSingular}.
        </p>
      </div>
      
      {/* Mobile Tab Selector */}
      <div className="relative grid grid-cols-1 sm:hidden">
        <select
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value as 'general' | 'week')}
          aria-label="Select a tab"
          className="w-full appearance-none rounded-full border border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] py-2.5 pl-4 pr-10 text-base font-semibold text-white outline-none focus:ring-2 focus:ring-[#FDC300]/50"
        >
          {tabs.map((tab) => (
            <option key={tab.key} value={tab.key} className="bg-[var(--rtbf-blue-dark,#003E63)] text-white">
              {tab.name}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-white/80"
        />
      </div>
      
      {/* Desktop Tabs */}
      <div className="hidden sm:block">
        <nav aria-label="Tabs" className="-mb-px flex flex-wrap gap-6 border-b border-white/20">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key as 'general' | 'week')}
              aria-current={tab.current ? 'page' : undefined}
              className={classNames(
                tab.current
                  ? "border-[#FDC300] text-white"
                  : "border-transparent text-white/70 hover:border-white/40 hover:text-white",
                "whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-semibold transition-colors",
              )}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>
      
      {/* Filters */}
      <div className="flex w-full flex-col gap-4">        
          {/* Week Selection (only shown for week tab) */}
          {activeTab === 'week' && (
            <div className="w-full">
              <Select
                onValueChange={(value) => {
                  setPageNumber(1);
                  value === "unset" ? setSelectedWeekId(null) : setSelectedWeekId(Number(value));
                }}
                value={selectedWeekId !== null ? String(selectedWeekId) : "unset"}
              >
                <SelectTrigger
                  aria-label={copy.raceLabelTitle}
                  className="w-full border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] text-white"
                >
                  <SelectValue placeholder={`Choisissez une ${copy.raceLabelSingular}`} />
                </SelectTrigger>
                <SelectContent>
                  {/* Option to unset the week */}
                  <SelectItem key="unset" value="unset">
                    Aucune {copy.raceLabelSingular} sélectionnée
                  </SelectItem>
                  {competitionData.weeks.map((week: any) => (
                    <SelectItem key={week.weekId} value={String(week.weekId)}>
                      {week.weekId}.{" "}
                      {competitionData.matches.find(
                        (match: any) => match.weekId === week.weekId
                      )?.feedUrl}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
      </div>
      
      {/* Rankings Table */}
      <div className="overflow-hidden rounded-md text-white">
        <Table className="min-w-full text-sm">
          <TableHeader className="bg-[var(--rtbf-blue,#0076A9)] [&_tr]:border-[var(--rtbf-blue,#0076A9)]">
            <TableRow className="border-b-0 hover:bg-transparent">
              <TableHead className="w-16 py-3 font-medium text-white"></TableHead>
              <TableHead className="py-3 font-medium text-white">Nom</TableHead>
              <TableHead className="py-3 text-right font-medium text-white">Points</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow className="border-b border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] hover:bg-[var(--rtbf-blue-dark,#003E63)]">
                <TableCell colSpan={3} className="py-8 text-center text-white">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    <span className="text-white/80">Chargement...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : rankings.length === 0 ? (
              <TableRow className="border-b border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] hover:bg-[var(--rtbf-blue-dark,#003E63)]">
                <TableCell colSpan={3} className="py-6 text-center text-white/90">
                  Aucun résultat
                </TableCell>
              </TableRow>
            ) : (
              rankings.map((data, index) => {
                const rowBg = index % 2 === 0 ? "bg-[var(--rtbf-blue-dark,#003E63)]" : "bg-[var(--rtbf-blue,#0076A9)]";
                return (
                  <TableRow
                    key={`team_${data.teamId || index}`}
                    className={classNames(rowBg, "border-b border-[var(--rtbf-blue,#0076A9)] text-white hover:bg-white/10")}
                  >
                    <TableCell className="font-medium text-white">{data.rank}</TableCell>
                    <TableCell>
                      <a href={`/fantasy/public/${data.teamId}`} className="font-medium text-white hover:text-[#FDC300] hover:underline">
                        {data.teamName}
                      </a>
                    </TableCell>
                    <TableCell className="text-right font-medium text-white">{data.points}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Pagination */}
      <div className="flex flex-col items-center justify-end gap-4 p-2 sm:flex-row">
        <p className="text-sm text-white/90">
          Affichage de{" "}
          <span className="font-medium text-white">{(pageNumber - 1) * RECORDS_PER_PAGE + 1}</span>{" "}
          à{" "}
          <span className="font-medium text-white">{Math.min(pageNumber * RECORDS_PER_PAGE, totalRecords)}</span>{" "}
          sur <span className="font-medium text-white">{totalRecords}</span> résultats
        </p>
        <nav className="flex items-center gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            className="bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200"
            onClick={() => setPageNumber(1)}
            disabled={pageNumber === 1}
          >
            1
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200"
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[2.5rem] rounded-md bg-[var(--rtbf-blue,#0076A9)] px-3 py-1.5 text-center text-sm font-bold text-white">
            {pageNumber}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200"
            onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
            disabled={pageNumber === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200"
            onClick={() => setPageNumber(totalPages)}
            disabled={pageNumber === totalPages}
          >
            {totalPages}
          </Button>
        </nav>
      </div>
      </div>
    </div>
  );
}
